import { action, computed, makeObservable, observable } from 'mobx';

import type { FieldValidator } from 'widget/utils/validators';

export type FormFieldModelOptions<T = string> = {
  validators?: FieldValidator<T>[];
};

export default class FormFieldModel<T = string> {
  private _value: T;
  private _initialValue: T;
  private _error: string | null = null;
  private _touched = false;
  private readonly _validators: FieldValidator<T>[];

  constructor(initialValue: T, options?: FormFieldModelOptions<T>) {
    this._value = initialValue;
    this._initialValue = initialValue;
    this._validators = options?.validators ?? [];
    this._error = this._runValidators(initialValue);

    makeObservable<
      FormFieldModel<T>,
      '_value' | '_initialValue' | '_touched' | '_error'
    >(this, {
      _value: observable,
      _initialValue: observable,
      _touched: observable,
      _error: observable,

      value: computed,
      initialValue: computed,
      error: computed,
      isValid: computed,
      touched: computed,

      changeValue: action.bound,
      setTouched: action.bound,
      setInitialValue: action.bound,
      reset: action.bound
    });
  }

  private _runValidators(value: T): string | null {
    for (const validator of this._validators) {
      const error = validator(value);

      if (error !== null) {
        return error;
      }
    }

    return null;
  }

  get value(): T {
    return this._value;
  }

  get initialValue(): T {
    return this._initialValue;
  }

  get error(): string | null {
    if (!this._touched || this._validators.length === 0) {
      return null;
    }

    return this._error;
  }

  get isValid(): boolean {
    if (this._validators.length === 0) {
      return true;
    }

    return this._runValidators(this._value) === null;
  }

  get touched(): boolean {
    return this._touched;
  }

  changeValue(value: T): void {
    this._value = value;
    this._error = this._runValidators(value);
    this._touched = this._value === this._initialValue ? false : true;
  }

  setInitialValue(value: T): void {
    this._initialValue = value;
  }

  setTouched(touched = true): void {
    this._touched = touched;
  }

  reset(): void {
    this._value = this._initialValue;
    this._error = null;
    this._touched = false;
  }
}
